import { useEffect, useRef, useState } from "react";
import { fetchPlaybackSource } from "@/services/api/player";

/** Refresh this long before the signed embed URL expires. */
export const EMBED_REFRESH_LEAD_MS = 10 * 60_000;
/** While the video is playing, look again this often instead of swapping the iframe under it. */
export const EMBED_RECHECK_MS = 60_000;
/** Never ask for a new URL sooner than this after the last one (a bad expiresAt can't loop). */
export const EMBED_MIN_REFRESH_MS = 30_000;

type Options = {
  /** LRN-16: receives the API error (none when the lesson is not a Bunny lesson). */
  onError?: (err?: unknown) => void;
  /**
   * Whether the student is mid-playback. A new embed URL reloads Bunny's iframe, so it is only
   * swapped in while paused; otherwise the swap waits. LEARN-11: also in a background tab — a
   * lecture listened to there keeps playing on the URL it already loaded.
   */
  isPlaying?: () => boolean;
  now?: () => number;
};

/**
 * LRN-23: Bunny's embed URL is signed and expires (6 h by default). A lesson left open past that
 * — paused overnight, or a tab returned to the next day — could not resume. This keeps a fresh URL:
 * it re-asks /playback-source shortly before `expiresAt` and swaps it in at a safe moment.
 * S3 F-8: a timer does not run while the laptop sleeps (and is throttled in a background tab), so
 * when the page becomes visible or focused again with the link within its refresh lead (or
 * expired) and the video paused, it refreshes at once — before the student presses play on a
 * dead link. Used by `BunnyLessonPlayer`.
 */
export function useBunnyEmbedSource(lessonId: string, options: Options = {}) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const now = () => optionsRef.current.now?.() ?? Date.now();

    const schedule = (expiresAt: string) => {
      const remaining = new Date(expiresAt).getTime() - now();
      // LEARN-11: with a short TTL (under 20 min) the fixed lead put the refresh in the past, so
      // every load scheduled another at once — a request loop. At most half the lifetime early.
      const lead = Math.min(EMBED_REFRESH_LEAD_MS, Math.max(0, remaining) / 2);
      timer = setTimeout(maybeRefresh, Math.max(EMBED_MIN_REFRESH_MS, remaining - lead));
    };

    const maybeRefresh = () => {
      if (cancelled) return;
      if (optionsRef.current.isPlaying?.()) {
        timer = setTimeout(maybeRefresh, EMBED_RECHECK_MS);
        return;
      }
      void load();
    };

    let expiresAtMs: number | null = null;
    const onReturn = () => {
      if (cancelled || expiresAtMs === null || document.visibilityState === "hidden") return;
      const remaining = expiresAtMs - now();
      if (remaining > EMBED_REFRESH_LEAD_MS / 2) return;
      if (optionsRef.current.isPlaying?.()) return;
      if (timer) clearTimeout(timer);
      timer = null;
      expiresAtMs = null; // one refresh per return
      void load();
    };

    const load = async () => {
      try {
        const source = await fetchPlaybackSource(lessonId);
        if (cancelled) return;
        if (source.kind !== "bunny") {
          optionsRef.current.onError?.();
          return;
        }
        setEmbedUrl(source.embedUrl);
        expiresAtMs = new Date(source.expiresAt).getTime();
        schedule(source.expiresAt);
      } catch (err) {
        if (!cancelled) optionsRef.current.onError?.(err);
      }
    };

    setEmbedUrl(null);
    void load();
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [lessonId]);

  return { embedUrl };
}
